'use client';

/* eslint-disable react/no-array-index-key */
/* eslint-disable react/require-default-props */

import { memo, useState } from 'react';

import Image from 'next/image';

import {
  Popover, PopoverTrigger, PopoverContent, Card,
} from '@heroui/react';
import { motion } from 'framer-motion';

import { IoIosArrowDown } from 'react-icons/io';

import { truncateText } from '@/features/v2/adapters/texts';

import { useLiveBets } from '@/features/v2/casino/hooks/useLiveBets';

import { useAuthStore } from '@/stores/userStore';

import { moneyFormat, normalizeImageUrl } from '@/utils/core';

import { getCurrencySymbol } from '@/utils/currency';

import core from './localization/core';

const LiveBets = ({ instanceId }: { instanceId?: string } = {}) => {
  const {
    games,
    isLoading: loading,
    error,
    activeFilter,
    // isFakeData,
    changeFilter,
    // refetch,
  } = useLiveBets(instanceId);

  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const { account } = useAuthStore();
  const handleImageError = (gameId: string) => {
    setImageErrors((prev) => { return new Set(prev).add(gameId); });
  };

  const generateMultiplier = () => {
    const min = 1.01;
    const max = 2.5;
    return (Math.random() * (max - min) + min).toFixed(2);
  };

  if (loading) {
    return (
      <section className="px-4 md:px-0 mt-6 max-w-7xl mx-auto">
        <div className="bg-default-100 rounded-2xl p-4 border border-[#0d1117]">
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => {
              return (
                <div
                  key={`skeleton-${i}`}
                  className="flex items-center gap-4 rounded-xl p-4 bg-hbackground animate-pulse"
                  style={{
                    animationDelay: `${i * 100}ms`,
                  }}
                >
                  <div className="w-9 h-9 rounded-xl bg-gray-600" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-600 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-600 rounded w-1/2" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="px-4 mt-6">
        <div className="bg-red-100 rounded-2xl p-4 border border-red-300">
          <p className="text-red-500">
            Error:
            {error}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 md:px-0 mt-6 md:mt-10 max-w-7xl mx-auto">
      <header className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Image
            src={core.liveBets.img}
            alt={core.liveBets.iconAlt}
            width={30}
            height={30}
            loading="lazy"
            quality={70}
            className="w-auto h-auto"
          />
          <Popover showArrow placement="bottom" className="z-0">
            <PopoverTrigger>
              <h2 className="text-base font-bold text-white">
                {activeFilter === 'live' && core.liveBets.title}
                {activeFilter === 'highRollers' && core.liveBets.filters.highRollers}
              </h2>
            </PopoverTrigger>
            <PopoverContent>
              <p>
                {activeFilter === 'live' && core.liveBets.filterDescriptions.live}
                {activeFilter === 'highRollers' && core.liveBets.filterDescriptions.highRollers}
              </p>
            </PopoverContent>
          </Popover>
        </div>
        <Popover showArrow placement="bottom" className="z-0">
          <PopoverTrigger>
            <div aria-label="Ver más" className="flex items-center gap-2">
              <p className="text-sm font-bold font-text bg-linear-to-t from-hgradient-50 to-hgradient-100 text-transparent bg-clip-text">
                {core.liveBets.textLink}
              </p>
              <IoIosArrowDown className="text-sm font-bold text-hgradient-100" />
            </div>
          </PopoverTrigger>
          <PopoverContent>
            <Card className="max-w-[240px] bg-hbackground rounded-lg px-4 py-5">
              <button
                type="button"
                onClick={() => { return changeFilter('highRollers'); }}
                className={`w-full text-left font-medium gap-3 text-sm transition-colors ${
                  activeFilter === 'highRollers' ? 'text-hprimary-500' : 'text-default-900 hover:text-hprimary-500'
                }`}
              >
                {core.liveBets.filters.highRollers}
              </button>
              <hr className="border-hdivider w-full h-1 my-3" />
              <button
                type="button"
                onClick={() => { return changeFilter('live'); }}
                className={`w-full text-left font-medium gap-3 text-sm transition-colors ${
                  activeFilter === 'live' ? 'text-hprimary-500' : 'text-default-900 hover:text-hprimary-500'
                }`}
              >
                {core.liveBets.filters.live}
              </button>
            </Card>
          </PopoverContent>
        </Popover>
      </header>

      <div className="bg-default-50 rounded-lg p-4">
        <table className="w-full">
          <thead className="relative">
            <tr className="flex items-center justify-between w-full gap-6 rounded-xl px-6 py-4 my-2">
              <th scope="col" className="text-white text-sm md:text-lg font-normal text-center flex-1 font-title">
                {core.liveBets.table.title}
              </th>
              <th scope="col" className="text-white text-sm md:text-lg font-normal text-center flex-1 font-title">
                {core.liveBets.table.multiplier}
              </th>
              <th scope="col" className="text-white text-sm md:text-lg font-normal text-center flex-1 font-title">
                {core.liveBets.table.winnings}
              </th>
            </tr>
            <div className="fade-bottom" />
          </thead>
          <tbody className="h-80 overflow-hidden relative block">
            <motion.tr
              className="block"
              animate={{
                y: [0, -320],
              }}
              transition={{
                duration: 20,
                repeat: Number.POSITIVE_INFINITY,
                ease: [1.01, 1.01, 1.01, 1.01],
              }}
            >
              {games.map((game, i) => {
                const hasImageError = imageErrors.has(game.id);
                const imageUrl = hasImageError ? '/img/v2/casino/onError.webp' : normalizeImageUrl(game.thumb_url);

                const multiplier = generateMultiplier();

                return (
                  <motion.td
                    key={`game-${game.id}-${i}`}
                    className="flex items-center justify-between gap-3 md:gap-6 rounded-xl pl-2 pr-6 py-4 my-2 bg-hbackground transition-all duration-300 hover:border-hprimary-500/30 min-h-[72px] "
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="flex items-center gap-4 flex-1 justify-center">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                        <Image
                          src={imageUrl || '/placeholder.svg'}
                          alt={game.i_gamedesc || 'Game image'}
                          width={48}
                          height={48}
                          className="object-cover rounded-lg"
                          onError={() => { return handleImageError(game.id); }}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h5 className="text-hprimary-500 md:font-semibold text-xs leading-tight truncate md:text-lg">
                          {truncateText(game.identifier, 11)}
                        </h5>
                        <p className="text-xs text-gray-500 text-center my-1">
                          {truncateText(game.nick, 8)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 bg-default-50 rounded-lg px-3 py-1 min-w-[80px] justify-center flex-1 md:max-w-[200px]">
                      <span className="text-white font-medium text-sm md:text-lg">{multiplier}</span>
                      <motion.div
                        className="w-2 h-2 bg-green-500 rounded-full shadow-xs shadow-green-500/50"
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                      />
                    </div>

                    <div className="flex flex-col items-center text-hgradient-50 flex-1 justify-center text-sm md:text-lg">
                      +
                      {getCurrencySymbol(account.currency ?? 'USD')}
                      {' '}
                      {moneyFormat(game.amount)}
                    </div>
                  </motion.td>
                );
              })}
            </motion.tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default memo(LiveBets);
